import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Play, Pause, Music, Send, Radio, Target, Gift, Clock, Sparkles, Flag } from "lucide-react";
import { motion } from "framer-motion";
import { api, mediaUrl } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import VerifiedBadge from "@/components/VerifiedBadge";
import { goalPct, milestoneMsg, deadlineInfo, MILESTONES, money } from "@/lib/tipgoal";

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}

export function Avatar({ artist, size = 44 }) {
  return artist?.avatar_url ? (
    <img src={mediaUrl(artist.avatar_url)} alt="" style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" />
  ) : (
    <span style={{ width: size, height: size }} className="rounded-full bg-violet-500/20 flex items-center justify-center text-violet-300 font-bold shrink-0">
      {artist?.artist_name?.[0]?.toUpperCase() || "A"}
    </span>
  );
}

function EmbeddedTrack({ track }) {
  const { playTrack, current, isPlaying, togglePlay } = usePlayer();
  const active = current?.id === track.id;
  return (
    <div className="mt-3 flex items-center gap-3 p-3 rounded-xl bg-black/40 border border-white/10" data-testid="post-embedded-track">
      {track.cover_url ? <img src={mediaUrl(track.cover_url)} alt="" className="w-14 h-14 rounded-lg object-cover" />
        : <span className="w-14 h-14 rounded-lg bg-emerald-500/15 flex items-center justify-center"><Music className="w-6 h-6 text-emerald-400" /></span>}
      <div className="min-w-0 flex-1">
        <p className="font-bold text-sm truncate">{track.title}</p>
        <p className="text-xs text-zinc-500 font-mono-nu uppercase tracking-wider">{track.genre}</p>
      </div>
      <button data-testid="post-track-play" onClick={() => (active ? togglePlay() : playTrack(track, [track]))}
        className="w-11 h-11 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black flex items-center justify-center transition-colors shrink-0">
        {active && isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
      </button>
    </div>
  );
}

export default function PostCard({ post }) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(post.liked);
  const [likes, setLikes] = useState(post.likes);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [commentCount, setCommentCount] = useState(post.comments);
  const [text, setText] = useState("");

  const toggleLike = async () => {
    setLiked(!liked); setLikes(likes + (liked ? -1 : 1));
    try { const { data } = await api.post(`/like/post/${post.id}`); setLiked(data.liked); setLikes(data.likes); } catch {}
  };

  const openComments = async () => {
    setShowComments(!showComments);
    if (!comments) {
      try { const { data } = await api.get(`/comments/post/${post.id}`); setComments(data); } catch {}
    }
  };

  const addComment = async () => {
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/comments/post/${post.id}`, { text });
      setComments([...(comments || []), data]); setCommentCount(commentCount + 1); setText("");
    } catch {}
  };

  const a = post.artist;
  return (
    <motion.article initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className="nu-card p-5" data-testid="feed-post">
      <div className="flex items-center gap-3">
        <Link to={`/artist/${a?.id}`}><Avatar artist={a} /></Link>
        <div className="min-w-0">
          <Link to={`/artist/${a?.id}`} className="font-bold text-sm hover:text-emerald-400 transition-colors inline-flex items-center gap-1">{a?.artist_name}<VerifiedBadge show={a?.is_subscribed} size={14} /></Link>
          <p className="text-xs text-zinc-500 font-mono-nu">@{a?.handle} · {timeAgo(post.created_at)}</p>
        </div>
      </div>

      {post.text && <p className="mt-3 text-zinc-200 whitespace-pre-wrap leading-relaxed">{post.text}</p>}
      {post.image_url && <img src={mediaUrl(post.image_url)} alt="" className="mt-3 rounded-xl w-full object-cover max-h-96" />}
      {post.track && <EmbeddedTrack track={post.track} />}
      {post.video && (
        <video src={mediaUrl(post.video.video_url)} poster={mediaUrl(post.video.thumb_url)} controls
          className="mt-3 rounded-xl w-full max-h-96 bg-black" data-testid="post-embedded-video" />
      )}
      {post.party && (
        <div className="mt-3 p-4 rounded-xl bg-black/40 border border-white/10" data-testid="post-recap">
          <div className="flex items-center gap-2 text-xs font-mono-nu uppercase tracking-widest text-emerald-400 mb-3">
            <Radio className="w-3.5 h-3.5" /> Listen Party Recap · {post.party.track_count} track{post.party.track_count > 1 ? "s" : ""}
          </div>
          <div className="flex -space-x-2">
            {post.party.tracks?.map((tr, i) => (
              tr.cover_url ? <img key={i} src={mediaUrl(tr.cover_url)} alt="" className="w-14 h-14 rounded-lg object-cover ring-2 ring-[#181824]" />
                : <span key={i} className="w-14 h-14 rounded-lg bg-emerald-500/15 flex items-center justify-center ring-2 ring-[#181824]"><Music className="w-5 h-5 text-emerald-400" /></span>
            ))}
          </div>
          <p className="mt-3 text-sm text-zinc-400">{post.party.tracks?.map((tr) => tr.title).join(" · ")}</p>
        </div>
      )}

      {post.tip_goal_data && (() => {
        const g = post.tip_goal_data;
        const pct = goalPct(g.progress_cents, g.goal_cents);
        const dl = deadlineInfo(g.deadline);
        const ended = dl?.ended;
        const shout = milestoneMsg(pct);
        return (
          <div className="mt-3 p-4 rounded-xl bg-black/40 border border-emerald-500/25" data-testid="post-tip-goal">
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-sm font-semibold"><Target className="w-4 h-4 text-emerald-400" /> {g.label || "Tip Goal"}</span>
              <span className="text-xs font-mono-nu text-zinc-400">{money(g.progress_cents)} <span className="text-zinc-600">/ {money(g.goal_cents)}</span></span>
            </div>
            <div className="mt-3 relative h-2.5 rounded-full bg-white/5 overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-700 ${pct >= 100 ? "bg-amber-400" : "bg-gradient-to-r from-emerald-500 to-emerald-400"}`} style={{ width: `${pct}%` }} />
              {MILESTONES.slice(0, 3).map((m) => <span key={m} className="absolute top-0 bottom-0 w-px bg-black/50" style={{ left: `${m}%` }} />)}
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
              <p className="text-xs text-zinc-500 flex items-center gap-1">
                {ended ? <><Flag className="w-3.5 h-3.5" /> Goal ended</> : shout ? <><Sparkles className="w-3.5 h-3.5 text-emerald-400" /> {shout}</> : `${pct}% there this month`}
              </p>
              {dl && !ended && <span className={`text-[11px] font-mono-nu flex items-center gap-1 ${dl.daysLeft <= 2 ? "text-amber-400" : "text-zinc-500"}`}><Clock className="w-3 h-3" /> {dl.label}</span>}
            </div>
            {!ended && (
              <Link to={`/tip/${g.artist_id}`} data-testid="post-tip-cta"
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-sm transition-colors">
                <Gift className="w-4 h-4" /> Chip in a tip
              </Link>
            )}
          </div>
        );
      })()}

      <div className="mt-4 flex items-center gap-6 text-sm">
        <button onClick={toggleLike} data-testid="post-like-button"
          className={`flex items-center gap-2 transition-colors ${liked ? "text-red-500" : "text-zinc-400 hover:text-red-400"}`}>
          <Heart className={`w-5 h-5 ${liked ? "fill-red-500" : ""}`} /> <span className="font-mono-nu">{likes}</span>
        </button>
        <button onClick={openComments} data-testid="post-comment-button"
          className="flex items-center gap-2 text-zinc-400 hover:text-emerald-400 transition-colors">
          <MessageCircle className="w-5 h-5" /> <span className="font-mono-nu">{commentCount}</span>
        </button>
      </div>

      {showComments && (
        <div className="mt-4 pt-4 border-t border-white/10 space-y-3" data-testid="comments-section">
          {(comments || []).map((c) => (
            <div key={c.id} className="flex items-start gap-2">
              <Avatar artist={c.artist} size={32} />
              <div className="bg-black/40 rounded-xl px-3 py-2 flex-1">
                <p className="text-xs font-bold">{c.artist?.artist_name}</p>
                <p className="text-sm text-zinc-300">{c.text}</p>
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Avatar artist={user} size={32} />
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()}
              placeholder="Add a comment…" data-testid="comment-input"
              className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-emerald-500/50" />
            <button onClick={addComment} data-testid="comment-submit" className="w-9 h-9 rounded-full bg-emerald-500 text-black flex items-center justify-center shrink-0"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </motion.article>
  );
}
